import { useEffect, useState } from "react";
import { Funnel, X, Plus, Check } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

function ValuePicker({ dsId, column, selected, onApply, onRemove }) {
  const [values, setValues] = useState([]);
  const [local, setLocal] = useState(selected);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/datasources/${dsId}/values`, { params: { column } });
        setValues(data.values || []);
      } finally { setLoading(false); }
    })();
  }, [dsId, column]);

  const toggle = (v) => setLocal((l) => (l.includes(v) ? l.filter((x) => x !== v) : [...l, v]));

  return (
    <Popover defaultOpen>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 border border-primary/50 bg-primary/10 text-primary px-3 py-1.5 text-xs"
          data-testid={`filter-chip-${column}`}>
          <span className="font-medium">{column}</span>
          <span className="text-muted-foreground">{selected.length ? `(${selected.length})` : "semua"}</span>
          <X size={13} onClick={(e) => { e.stopPropagation(); onRemove(); }} className="hover:text-destructive" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0 bg-card border-border" align="start">
        <div className="max-h-60 overflow-y-auto p-1">
          {loading ? (
            <p className="p-3 text-xs text-muted-foreground">Memuat…</p>
          ) : values.map((v) => (
            <button key={v} onClick={() => toggle(v)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left hover:bg-secondary transition-colors">
              <span className={`w-4 h-4 border flex items-center justify-center ${local.includes(v) ? "bg-primary border-primary" : "border-border"}`}>
                {local.includes(v) && <Check size={11} weight="bold" className="text-white" />}
              </span>
              <span className="truncate">{v}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-border p-2">
          <Button size="sm" className="w-full h-7 rounded-lg text-xs" onClick={() => onApply(local)}
            data-testid={`apply-filter-${column}`}>Terapkan</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function FilterBar({ dsId, columns, filters, setFilters }) {
  const catCols = columns.filter((c) => c.type === "string" && (c.distinct == null || c.distinct <= 200));
  const activeCols = filters.map((f) => f.column);
  const available = catCols.filter((c) => !activeCols.includes(c.name));

  const addFilter = (col) => setFilters([...filters, { column: col, values: [] }]);
  const applyValues = (col, values) => setFilters(filters.map((f) => (f.column === col ? { ...f, values } : f)));
  const removeFilter = (col) => setFilters(filters.filter((f) => f.column !== col));

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="filter-bar">
      <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
        <Funnel size={15} /> Filter
      </span>
      {filters.map((f) => (
        <ValuePicker key={f.column} dsId={dsId} column={f.column} selected={f.values}
          onApply={(v) => applyValues(f.column, v)} onRemove={() => removeFilter(f.column)} />
      ))}
      {available.length > 0 && (
        <Select value="" onValueChange={addFilter}>
          <SelectTrigger className="h-8 w-auto gap-1.5 text-xs border-dashed" data-testid="add-filter-select">
            <Plus size={14} /> <span>Tambah filter</span>
          </SelectTrigger>
          <SelectContent>
            {available.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {filters.length > 0 && (
        <button onClick={() => setFilters([])} className="text-xs text-muted-foreground hover:text-destructive underline"
          data-testid="clear-filters">Reset</button>
      )}
    </div>
  );
}
